import { format } from 'date-fns'
import { CONSTANTS } from '../Constants/Constant'
import {
  allFilterOptions,
  filterConditions,
} from '../Constants/Constant/ListingPageConstant/ContentListingConstants'
import ListingColumns from '../Constants/Constant/Schema/managContent.json'
import AssetStatusListingCols from '../Constants/Constant/Schema/manageStatusListing.json'

interface MetaAttributeObj {
  id: string
  label: string
  dataType?: string
  fieldType: string
  fieldName: string
  isMultiple?: boolean
  assetCategory?: string
  assetType?: string
  metaCategory?: string
  sort?: boolean
  filter?: boolean
  position?: number
  viewInListing?: boolean
  class?: string
  createdAt?: string
  filterValueType?: string
  filterValueCondition?: Record<string, any>[]
  content?: boolean
}

interface HeadersObj {
  [key: string]: MetaAttributeObj
}

const processMetaAttributes = (element: any, headersObj: HeadersObj): void => {
  const listingColumnsCopy: any = JSON.parse(JSON.stringify(ListingColumns))
  element?.attributes?.forEach((attribute: any) => {
    attribute?.metaAttributes?.forEach((metaAttribute: any) => {
      const key = `metaAttributes.${metaAttribute?.fieldName}`
      const columnDef = listingColumnsCopy[metaAttribute.fieldName]
      if (!headersObj.key && columnDef?.column) {
        const { filterValueType, filterValueCondition } = getFilterOptions(columnDef, metaAttribute)
        const metaAttributeObj: MetaAttributeObj = {
          id: key, // To read data from rows
          label: metaAttribute?.label,
          dataType: metaAttribute?.dataType,
          fieldType: metaAttribute?.fieldType,
          fieldName: metaAttribute?.fieldName,
          isMultiple: metaAttribute?.isMultiple,
          sort: columnDef.sorting,
          filter: columnDef.filter,
          assetCategory: element?.assetCategory,
          assetType: element?.assetType,
          metaCategory: attribute?.metaCategory,
          position: columnDef?.position,
          class: element?.class || '',
          filterValueType,
          filterValueCondition,
        }
        headersObj[key] = { ...metaAttributeObj, viewInListing: columnDef.columnDefault }
      }
    })
  })
}

/**
 * Process meta attributes for the home page.
 * This function processes the given meta attributes and constructs the headers object.
 * @param {Array} attributes - The array of meta attributes to process.
 * @param {Object} headersObj - The headers object to populate.
 * @returns {void}
 */
export const processMetaAttributesForHomePage = (attributes: any, headersObj: HeadersObj): void => {
  const listingColumnsCopy: any = JSON.parse(JSON.stringify(AssetStatusListingCols))
  attributes?.forEach((metaAttribute: any) => {
    const key = metaAttribute?.fieldName
    const columnDef = listingColumnsCopy[metaAttribute.fieldName]
    if (!headersObj.key && columnDef?.column) {
      const { filterValueType, filterValueCondition } = getFilterOptions(columnDef, metaAttribute)
      const metaAttributeObj: MetaAttributeObj = {
        id: columnDef.key || key,
        label: metaAttribute?.label,
        dataType: metaAttribute?.dataType,
        fieldType: metaAttribute?.fieldType,
        fieldName: metaAttribute?.fieldName,
        isMultiple: metaAttribute?.isMultiple,
        sort: columnDef.sorting,
        filter: columnDef.filter,
        assetCategory: '',
        assetType: '',
        metaCategory: '',
        position: columnDef?.position,
        class: '',
        filterValueType,
        filterValueCondition,
      }
      headersObj[key] = { ...metaAttributeObj, viewInListing: columnDef.columnDefault }
    }
  })
}

export const processRecords = (
  records: any[],
  contentCategory: string | undefined,
  headersObj: HeadersObj,
): void => {
  records.forEach((element) => {
    if (contentCategory?.toUpperCase() === element?.contentCategory) {
      processMetaAttributes(element, headersObj)
    }
  })
}

export const processRecordsAdvanceSearch = (
  records: any[],
  attributes: any,
  headersObj: HeadersObj,
  attributeFilter = [],
  isAdvancedSearch = false,
): void => {
  processMetaAttributesAdvanceSearch(attributes, headersObj, attributeFilter, isAdvancedSearch)
}

const processMetaAttributesAdvanceSearch = (
  attributes: any,
  headersObj: HeadersObj,
  attributeFilter = [],
  isAdvancedSearch = false,
): void => {
  const filters = attributeFilter.map(
    (filter: { filterBy: string }) =>
      `${
        filter.filterBy?.includes('metaAttributes.')
          ? filter.filterBy
          : 'metaAttributes.' + filter.filterBy
      }`,
  )
  const excludeMetaAttributes = ['isBaseAsset', 'updatedAt']
  const listingColumnsCopy: any = { ...ListingColumns }
  attributes?.forEach((metaAttribute: any, _index: any) => {
    const key = !excludeMetaAttributes?.includes(metaAttribute?.fieldName)
      ? `metaAttributes.${metaAttribute?.fieldName}`
      : metaAttribute?.fieldName
    const columnDef = listingColumnsCopy[metaAttribute?.fieldName]
    if (!headersObj?.key) {
      const metaAttributeObj: MetaAttributeObj = {
        id: columnDef?.key || key,
        label: metaAttribute?.label,
        dataType: metaAttribute?.dataType,
        fieldType: metaAttribute?.fieldType,
        fieldName: metaAttribute?.fieldName,
        isMultiple: metaAttribute?.isMultiple,
        sort: columnDef?.sorting || false,
        filter: columnDef?.filter || false,
        assetCategory: '',
        assetType: '',
        metaCategory: '',
        // position: columnDef?.position,
        class: '',
        content: false,
      }

      headersObj[key] = {
        ...metaAttributeObj,
        viewInListing: columnDef?.columnDefault
          ? columnDef?.columnDefault
          : filters?.some((item) => item === key),
        filter: isAdvancedSearch ? false : metaAttributeObj.filter,
      }
    }
  })
}

export const getTableDataForAssetList = (headers: any, showVersionIcon: boolean) => {
  if (headers.length > 0) {
    if (showVersionIcon) {
      return [
        showVersionIcon
          ? { id: 'versionCount', fieldName: 'versionCount', label: '     ', sort: false }
          : {},
        { id: 'clone', fieldName: 'clone', label: '     ', sort: false },
        ...headers.filter((item: any) => item.viewInListing),
      ]
    } else {
      return [
        { id: 'clone', fieldName: 'clone', label: '     ', sort: false },
        ...headers.filter((item: any) => item.viewInListing),
      ]
    }
  }
  return []
}

export const parsedVersionDataForAS = (versionData: any, record: any) => {
  return versionData?.data?.records
    ?.map((version: any) => {
      if (version._id === record._id) return null
      const metaAttributes = version.ContentMetaCategory.reduce(
        (acc: any, attr: any) => ({ ...acc, ...attr.attributes }),
        {},
      )
      delete version.ContentMetaCategory
      return { ...version, metaAttributes }
    })
    .filter(Boolean)
}

export const getThumbnailFromImageDetails = (imageDetails: any) => {
  if (imageDetails?.landscapeThumb?.[0]?.imageKey) {
    return CONSTANTS.CLOUDFRONT_BASE_URL + '/' + imageDetails.landscapeThumb[0].imageKey
  }
  return ''
}

/**
 *
 * @param columnDef Defination of column
 * @param metaAttribute Details of attribute
 * @returns filterValueType and filterValueCondition for filtering the result
 */
export const getFilterOptions = (columnDef: any, metaAttribute: any) => {
  let filterValueType = '',
    filterValueCondition: any = []
  if (columnDef.filter) {
    if (metaAttribute?.fieldType === CONSTANTS.FIELDTYPES.DATETIME) {
      filterValueType = CONSTANTS.FIELDTYPES.DATETIME
      filterValueCondition = filterConditions[CONSTANTS.FIELDTYPES.DATETIME]
    } else if (metaAttribute?.dataType === CONSTANTS.FIELDTYPES.BOOLEAN) {
      filterValueType = CONSTANTS.FIELDTYPES.BOOLEAN
      filterValueCondition = filterConditions[CONSTANTS.FIELDTYPES.BOOLEAN]
    } else if (metaAttribute?.fieldType === CONSTANTS.FIELDTYPES.NUMBER) {
      filterValueType = CONSTANTS.FIELDTYPES.NUMBER
      filterValueCondition = filterConditions[CONSTANTS.FIELDTYPES.NUMBER]
    } else if (metaAttribute?.fieldType === CONSTANTS.FIELDTYPES.SELECT) {
      filterValueType = CONSTANTS.FIELDTYPES.TEXT
      filterValueCondition = filterConditions[CONSTANTS.FIELDTYPES.SELECT]
    } else if (metaAttribute?.fieldType === CONSTANTS.FIELDTYPES.FILE) {
      filterValueType = CONSTANTS.FIELDTYPES.FILE
      filterValueCondition = filterConditions[CONSTANTS.FIELDTYPES.FILE]
    } else {
      filterValueType = CONSTANTS.FIELDTYPES.TEXT
      filterValueCondition = filterConditions[CONSTANTS.FIELDTYPES.TEXT]
    }
  }
  return { filterValueType, filterValueCondition }
}

/**
 * Function to create request data
 * @param contentCategory selected category
 * @param selectedFilters selected filters
 * @param stateDetailsPage selecte page type
 * @returns list api data
 */
export const getGlobalSearchParams = ({
  contentCategory,
  selectedFilters,
  stateDetailsPage,
}: {
  contentCategory: string
  selectedFilters: any
  stateDetailsPage: boolean
}) => {
  const listingColumnsCopy: any = JSON.parse(JSON.stringify(ListingColumns))
  const { search, state, sort, sortType, limit, pageNumber, filters } = selectedFilters
  let params: any = {
    limit,
    page: pageNumber,
    sort: [],
    contentFilters: [],
    attributeFilters: [],
    assetGroupFilters: [],
    selectAttributes: {},
  }
  if (search) {
    params = { ...params, titleOrId: search }
  }
  if (contentCategory || state) {
    if (!stateDetailsPage && contentCategory) {
      params.contentFilters.push({
        filterBy: 'contentCategory',
        filterValue: contentCategory.toUpperCase(),
        filterCondition: 'equals',
      })
    }
    if (state.length) {
      params.contentFilters.push({
        filterBy: 'state',
        filterValue: state.length > 1 ? state : state[0],
        filterCondition: state.length > 1 ? 'in' : 'equals',
      })
    }
  }
  if (sort) {
    const { type, filterByChange } = listingColumnsCopy[sort] || { type: '' }
    let sortIn
    if (type === 'Metadata' || sort === 'parentId') {
      sortIn = 'attribute'
    } else if (type === 'AssetGroup') {
      sortIn = 'assetGroup'
    } else {
      sortIn = 'content'
    }
    params.sort.push({
      sortType: Number(sortType),
      sortBy: filterByChange ?? sort,
      sortIn,
    })
  }
  if (filters?.length) {
    const appliedFilters = filters.filter(
      (item: any) =>
        typeof item.filterValue === 'boolean' ||
        item.filterValue ||
        item.filterCondition === 'null' ||
        item.filterCondition === 'notNull',
    )
    if (appliedFilters.length) {
      appliedFilters.forEach((filter: any) => {
        const filterCopy = { ...filter }
        if (filterCopy.filterBy === 'assetTypeD') {
          filterCopy.filterBy = 'assetType'
        }
        if (filterCopy.filterBy === 'assetGroupId') {
          filterCopy.filterValue = Number(filterCopy.filterValue)
        }
        const { type, filterByChange } = listingColumnsCopy[filterCopy.filterBy] || {
          type: 'Blitz Property',
        }
        if (filterCopy.filterCondition === 'in') {
          filterCopy.filterValue = filterCopy.filterValue
            .split(',')
            .map((item: string) => item.trim())
        }
        if (type === 'Metadata' || type === 'AssetGroup') {
          const targetFilter =
            type === 'AssetGroup' ? params.assetGroupFilters : params.attributeFilters

          targetFilter.push({
            ...filterCopy,
            ...(filterCopy.filterCondition === 'null' && {
              filterValue: null,
              filterCondition: 'equals',
            }),
            ...(filterCopy.filterCondition === 'notNull' && {
              filterValue: null,
              filterCondition: 'notEquals',
            }),
            ...(filterByChange && { filterBy: filterByChange }),
          })
        } else if (filterCopy.filterBy === 'blitzParentContentId') {
          const isParentId = params.attributeFilters.find(
            (item: any) => item.filterBy === 'blitzParentContentId',
          )
          if (!isParentId) params.attributeFilters.push(filterCopy)
        } else if (filterCopy.filterBy === 'baseContentId') {
          params.attributeFilters.push(filterCopy)
        } else if (filterCopy.filterBy === 'parentId') {
          if (filterCopy.filterCondition === 'null') {
            params.attributeFilters.push({
              ...filterCopy,
              filterValue: null,
              filterCondition: 'equals',
            })
          } else if (filterCopy.filterCondition === 'notNull') {
            params.attributeFilters.push({
              ...filterCopy,
              filterValue: null,
              filterCondition: 'notEquals',
            })
          } else {
            params.attributeFilters.push(filterCopy)
          }
        } else {
          if (filterCopy.filterCondition === 'null') {
            params.contentFilters.push({
              ...filterCopy,
              filterValue: null,
              filterCondition: 'equals',
            })
          } else if (filterCopy.filterCondition === 'notNull') {
            params.contentFilters.push({
              ...filterCopy,
              filterValue: null,
              filterCondition: 'notEquals',
            })
          } else {
            params.contentFilters.push(filterCopy)
          }
        }
      })
    }
  }
  return params
}

/**
 * Function to format date
 * @param value value to be formatted
 * @param dateFormat date format
 * @returns
 */
export const formatListingDate = (value: string | Date, dateFormat: string) => {
  try {
    return format(new Date(value), dateFormat)
  } catch (_err) {
    return CONSTANTS.DEFAULT_TEXT
  }
}

/**
 * Function to format nested records for tabel listing
 * @param records List for records
 */
export const formatNestedRecords = (records: any) => {
  records.forEach((record: any) => {
    record['metaAttributes'] = {}
    record?.metaCategories?.forEach((item: any) => {
      record.metaAttributes = { ...record.metaAttributes, ...item }
    })
  })
}

export const removeDuplicatesByKey = (array: any, key: string) => {
  return array.reduce((accumulator: any, current: any) => {
    const existingItem = accumulator.find((item: any) => item[key] === current[key])
    if (!existingItem) {
      accumulator.push(current)
    }
    return accumulator
  }, [])
}

export const sortByKey = (array: any, key: string) => {
  return array.sort((a: any, b: any) => {
    if (a[key] < b[key]) return -1
    if (a[key] > b[key]) return 1
    return 0
  })
}

export const filterAssetTypesList = (
  records: any[],
  contentCategoryF?: string | undefined,
): any[] => {
  const filteredRecords = records
    .map(({ _id, assetType, assetCategory, contentCategory }) => ({
      _id,
      assetType,
      assetCategory,
      contentCategory,
      label: assetType,
    }))
    .filter(
      ({ contentCategory }) =>
        !contentCategoryF || contentCategory?.toLowerCase() === contentCategoryF.toLowerCase(),
    )
  return sortByKey(removeDuplicatesByKey(filteredRecords, 'assetType'), 'assetType')
}

export const updateHeadersArrayWithSaved = (headersArray: any, savedHeaders: any) => {
  if (savedHeaders?.length) {
    const savedHeadersObj = savedHeaders.reduce((prev: any, next: any) => {
      if (next.fieldName === 'assetType') {
        return { ...prev, [next.id]: !!next.viewInListing }
      }
      return { ...prev, [next.fieldName]: !!next.viewInListing }
    }, {})
    headersArray.forEach((element: any) => {
      element.viewInListing = !!savedHeadersObj[element.fieldName]
    })
  }
}

export const updateAssetTypesArrayWithSaved = (assetTypesCopy: any, savedAssetTypes: any) => {
  const savedAssetTypesObj = savedAssetTypes.reduce((prev: any, next: any) => {
    return { ...prev, [next.assetType]: !!next.select }
  }, {})

  assetTypesCopy.forEach((element: any) => {
    element.select = !!savedAssetTypesObj[element.assetType]
  })
}

export const getCommonFilterOptions = (fieldType: any, dataType: any, attr: any) => {
  const { DATETIME, DATE, NUMBER, TEXT, TIME, TEXTAREA, YEAR, SELECT, BOOLEAN, FILE } =
    CONSTANTS.FIELDTYPES

  let options = []
  if (attr?.label === 'Transcoding Process Type' || attr?.fieldName === 'mediaProfile') {
    options = [allFilterOptions.isEqualTo, allFilterOptions.isNotEqualTo]
  } else if (attr?.fieldName === 'subtitleLanguageFiles.subtitleLanguageFile') {
    options = [allFilterOptions.isEqualTo, allFilterOptions.contains, allFilterOptions.null]
  } else if (
    (fieldType === NUMBER && dataType === NUMBER) ||
    (fieldType === TEXT && dataType === NUMBER) ||
    (fieldType === TIME && dataType === NUMBER) ||
    (fieldType === SELECT && dataType === NUMBER) // Dobut attribute is available but not in BLITZ-6559
  ) {
    options = [
      allFilterOptions.isEqualTo,
      allFilterOptions.isNotEqualTo,
      allFilterOptions.isGreaterThan,
      allFilterOptions.isGreaterThanEquals,
      allFilterOptions.isLessThan,
      allFilterOptions.isLessThanEquals,
      allFilterOptions.null,
      allFilterOptions.notNull,
    ]
  } else if (fieldType === DATETIME && dataType === DATE) {
    options = [
      allFilterOptions.isEqualTo,
      allFilterOptions.isNotEqualTo,
      allFilterOptions.isGreaterThan,
      allFilterOptions.isGreaterThanEquals,
      allFilterOptions.isLessThan,
      allFilterOptions.isLessThanEquals,
      allFilterOptions.null,
      allFilterOptions.InBetween,
      allFilterOptions.notNull,
    ]
  } else if (
    (fieldType === TEXT && dataType === 'STRING') ||
    (fieldType === TEXTAREA && dataType === 'STRING')
  ) {
    options = [
      allFilterOptions.isEqualTo,
      allFilterOptions.isNotEqualTo,
      allFilterOptions.startsWith,
      allFilterOptions.endsWith,
      allFilterOptions.contains,
      // allFilterOptions.hasValue, // Doubt for removal (need to check with Akshit)
      allFilterOptions.null,
      allFilterOptions.notNull,
    ]
  } else if (fieldType === YEAR && dataType === NUMBER) {
    // Doubt As per BLITZ-6559 (it should be in TEXT/STRING)
    options = [
      allFilterOptions.isEqualTo,
      allFilterOptions.isNotEqualTo,
      // allFilterOptions.hasValue, // Doubt for removal (need to check with Akshit)
      allFilterOptions.null,
      allFilterOptions.notNull,
    ]
  } else if (fieldType === SELECT && dataType === 'STRING') {
    options = [
      allFilterOptions.isEqualTo,
      allFilterOptions.isNotEqualTo, // Doubt for addition (need to check with Akshit)
      allFilterOptions.startsWith, // Doubt for addition (need to check with Akshit)
      allFilterOptions.endsWith, // Doubt for addition (need to check with Akshit)
      allFilterOptions.contains, // Doubt for addition (need to check with Akshit)
      allFilterOptions.hasValue,
      allFilterOptions.null,
      allFilterOptions.notNull,
    ]
  } else if (fieldType === SELECT && dataType === BOOLEAN) {
    options = [
      allFilterOptions.isEqualTo,
      allFilterOptions.isNotEqualTo,
      allFilterOptions.null,
      allFilterOptions.notNull,
    ]
  } else if (fieldType === FILE && dataType === 'IMAGE_TYPE') {
    options = [
      allFilterOptions.isEqualTo,
      allFilterOptions.contains,
      allFilterOptions.null,
      allFilterOptions.notNull,
    ]
  } else {
    options = [allFilterOptions.isEqualTo, allFilterOptions.null, allFilterOptions.notNull]
  }

  return options
}

export const filterAdvanceSearchFields = (fields: any = []) => {
  const { DATETIME, DATE, NUMBER, TEXT, TIME, TEXTAREA, YEAR, SELECT, BOOLEAN, FILE, COMPLEXTYPE } =
    CONSTANTS.FIELDTYPES

  const filteredFields: any = []
  const filteredFieldsIndex: any = {}
  const filteredComplexFields: any = []
  const filteredComplexFieldsIndex: any = {}
  const finalFilteredFields: any = []
  const referencedIds = new Set()

  fields.filter((item: any) => {
    const { fieldType, dataType } = item
    if (
      (fieldType === DATETIME && dataType === DATE) ||
      (fieldType === NUMBER && dataType === NUMBER) ||
      (fieldType === TEXT && dataType === NUMBER) ||
      (fieldType === TIME && dataType === NUMBER) ||
      (fieldType === SELECT && dataType === NUMBER) || // Dobut attribute is available but not in BLITZ-6559
      (fieldType === TEXT && dataType === 'STRING') ||
      (fieldType === TEXTAREA && dataType === 'STRING') ||
      (fieldType === YEAR && dataType === NUMBER) || // Doubt As per BLITZ-6559 (it should be in TEXT/STRING)
      (fieldType === SELECT && dataType === 'STRING') ||
      (fieldType === SELECT && dataType === BOOLEAN)
    ) {
      filteredFields.push(item)
      filteredFieldsIndex[item._id] = filteredFields.length
    } else if (fieldType === COMPLEXTYPE) {
      filteredComplexFields.push(item)
    } else if (fieldType === FILE && dataType === 'IMAGE_TYPE') {
      filteredFields.push({ ...item, fieldName: `${item.fieldName}.imageKey` })
      filteredFieldsIndex[item._id] = filteredFields.length
    }
  })
  filteredComplexFields.forEach((item: any) => {
    item.attributeRef?.forEach((attribute: any) => {
      if (filteredFieldsIndex[attribute._id]) {
        referencedIds.add(attribute._id)
        const field = { ...filteredFields[filteredFieldsIndex[attribute._id] - 1] }
        const complexField = {
          ...field,
          _id: `${item._id}.${field._id}`,
          label: `${field.label} (${item.label})`,
          fieldName: `${item.fieldName}.${field.fieldName}`,
        }
        const position = filteredFieldsIndex[attribute._id] - 1
        if (filteredComplexFieldsIndex[position]) {
          filteredComplexFieldsIndex[position].push(complexField)
        } else {
          filteredComplexFieldsIndex[position] = [complexField]
        }
      }
    })
  })
  filteredFields.forEach((item: any, index: number) => {
    finalFilteredFields.push(item)
    if (filteredComplexFieldsIndex[index]) {
      filteredComplexFieldsIndex[index].forEach((complexField: any) => {
        finalFilteredFields.push(complexField)
      })
    }
  })
  return finalFilteredFields.filter((attr: any) => !referencedIds.has(attr._id))
}

export const isLiveB2BAssetEligible = (
  contentCategory: string | undefined,
  assetType: string | undefined,
) => {
  return (
    (contentCategory === 'TV_SHOW' && assetType === 'LIVE_EVENT') ||
    (contentCategory === 'SPORTS' && assetType === 'LIVE_SPORT')
  )
}

export const isLiveB2BAsset = (content: any) => {
  return content?.isLiveB2B && isLiveB2BAssetEligible(content?.contentCategory, content?.assetType)
}

export const isLiveNotB2BAsset = (content: any) => {
  return CONSTANTS.LIVE_OPTIONS.includes(content?.assetType) && !isLiveB2BAsset(content)
}
